import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Plus, Trash2, ChevronDown } from 'lucide-react';
import { rateChartAPI } from '../services/api';

// ESTIMATE Form - Exact replica of paper form with auto-fetch rates
const EstimateForm = forwardRef(({ initialData, jobCard, config, onChange }, ref) => {
  const [formData, setFormData] = useState({
    estimate_number: '',
    job_card_number: jobCard?.job_card_number || '',
    name: jobCard?.customer_name || '',
    reference: jobCard?.reference || '',
    date: new Date().toISOString().split('T')[0],
    estimate_lines: [],
    total_amount: 0,
    ...initialData
  });

  const [rateChart, setRateChart] = useState([]);
  const [autoFillDone, setAutoFillDone] = useState(false);

  // Load rate chart on mount
  useEffect(() => {
    const loadRateChart = async () => {
      try {
        const rates = await rateChartAPI.getAll();
        setRateChart(rates);
      } catch (error) {
        console.error('Failed to load rate chart:', error);
      }
    };
    loadRateChart();
  }, []);

  // Predefined particulars from paper estimate
  const PARTICULARS = config?.estimate_particulars || [
    'PRESSING WORK NORMAL',
    'PRESSING WORK HOT',
    'CUTTING WORK',
    'EDGE BANDING (22*.8)',
    'EDGE BANDING (22*2)',
    'EDGE BANDING (45)',
    'HANDLE PROFILE WORK',
    'DOUBLE WORK M',
    'DOUBLE WORK L',
    'DOUBLE WORK XL',
    'HINGES HOLE',
    'CUTTING LIST PREPRATION',
    'MINI FIX HOLE CUTTING',
    'PACKING',
    'HANDLING CHARGE',
    'HANDLE PROFILE',
    'ROTATING TV UNIT WORK',
    'BORING WORK',
    'GLASS DOOR CUTTING',
    'MICA CUTTING',
    'TRANSPORTATION',
    'MEASUREMENT CHARGE'
  ];

  useImperativeHandle(ref, () => ({
    getData: () => ({
      ...formData,
      estimate_lines: formData.estimate_lines.map((line, idx) => ({
        ...line,
        sl_no: idx + 1,
        total: (parseFloat(line.qty) || 0) * (parseFloat(line.rate) || 0)
      })),
      total_amount: calculateTotal()
    }),
    validate: () => formData.name && formData.estimate_lines.length > 0
  }));

  useEffect(() => {
    if (onChange) onChange(formData);
  }, [formData, onChange]);

  // Find rate from rate chart by matching description
  const findRateForParticular = (particular) => {
    if (!particular || rateChart.length === 0) return 0;
    
    // Normalize the search term
    const searchTerm = particular.toUpperCase().trim();
    
    // Try exact match first
    let match = rateChart.find(r => r.description.toUpperCase().trim() === searchTerm);
    
    // Try partial match if no exact match
    if (!match) {
      // Map common variations
      const mappings = {
        'PRESSING WORK NORMAL': ['PRESSING WORK', 'PRESSING'],
        'PRESSING WORK HOT': ['VENEER HOT PRESSING', 'HOT PRESSING'],
        'CUTTING WORK': ['CUTTING WORK', 'CUTTING'],
        'EDGE BANDING (22*.8)': ['EDGE BANDING WORK', 'EDGE BANDING'],
        'EDGE BANDING (22*2)': ['2MM EDGEBAND WORK', '2MM EDGE'],
        'EDGE BANDING (45)': ['45MM EDGEBAND WORK', '45MM EDGE'],
        'HANDLE PROFILE WORK': ['HANDLE PROFILE WORK', 'HANDLE PROFILE'],
        'HINGES HOLE': ['HINGES HOLE', 'HINGE'],
        'MINI FIX HOLE CUTTING': ['MINI FIX HOLE WORK', 'MINIFIX'],
        'MINIFIX HOLE CUTTING': ['MINI FIX HOLE WORK', 'MINIFIX'],
        'PACKING': ['PACKING CHARGES', 'PACKING'],
        'HANDLING CHARGE': ['HANDLING CHARGES', 'HANDLING'],
        'GLASS DOOR CUTTING': ['GLASS DOOR CUTTING', 'GLASS DOOR'],
        'MICA CUTTING': ['MICA CUTTING'],
        'CUTTING LIST PREPARATION': ['CUTTING LIST PREPARATION'],
        'CUTTING LIST PREPRATION': ['CUTTING LIST PREPARATION'],
        'TRIMMING': ['TRIMMING'],
        'BORING WORK': ['BORING WORK', 'BORING'],
        'GROOVING': ['SINGLE GROOVING', 'GROOVING'],
        'EDGE PROFILE WORK': ['EDGE PROFILE WORK'],
        'MITRE CUTTING': ['MITRE CUTTING']
      };
      
      const variations = mappings[searchTerm] || [searchTerm];
      for (const v of variations) {
        match = rateChart.find(r => 
          r.description.toUpperCase().includes(v.toUpperCase()) ||
          v.toUpperCase().includes(r.description.toUpperCase())
        );
        if (match) break;
      }
    }
    
    return match ? match.rate : 0;
  };

  // Auto-fill from job card when rate chart is loaded (only once)
  useEffect(() => {
    if (rateChart.length > 0 && jobCard && !autoFillDone && formData.estimate_lines.length === 0) {
      console.log('[EstimateForm] Auto-filling from job card');
      populateFromJobCardAuto();
      setAutoFillDone(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateChart, jobCard, autoFillDone]);

  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowAddDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const addLine = (particular = '') => {
    const rate = findRateForParticular(particular);
    setFormData(prev => ({
      ...prev,
      estimate_lines: [...prev.estimate_lines, {
        sl_no: prev.estimate_lines.length + 1,
        particular: particular,
        qty: '',
        rate: rate > 0 ? rate.toString() : '',
        total: 0,
        isCustom: false
      }]
    }));
    setShowAddDropdown(false);
  };

  const addCustomLine = () => {
    setFormData(prev => ({
      ...prev,
      estimate_lines: [...prev.estimate_lines, {
        sl_no: prev.estimate_lines.length + 1,
        particular: '',
        qty: '',
        rate: '',
        total: 0,
        isCustom: true
      }]
    }));
    setShowAddDropdown(false);
  };

  const updateLine = (index, field, value) => {
    setFormData(prev => {
      const lines = [...prev.estimate_lines];
      lines[index] = { ...lines[index], [field]: value };
      // A new item takes its unit from the rate chart when saved
      if (field === 'particular') lines[index].unit = '';
      
      // Auto-fetch rate when particular changes
      if (field === 'particular' && value) {
        const rate = findRateForParticular(value);
        if (rate > 0 && !lines[index].rate) {
          lines[index].rate = rate.toString();
        }
      }
      
      // Auto-calculate total
      lines[index].total = (parseFloat(lines[index].qty) || 0) * (parseFloat(lines[index].rate) || 0);
      return { ...prev, estimate_lines: lines };
    });
  };

  const removeLine = (index) => {
    setFormData(prev => ({
      ...prev,
      estimate_lines: prev.estimate_lines.filter((_, i) => i !== index)
    }));
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateTotal = () => {
    return formData.estimate_lines.reduce((sum, line) => {
      return sum + ((parseFloat(line.qty) || 0) * (parseFloat(line.rate) || 0));
    }, 0);
  };

  // Auto-populate from job card data (called automatically on load)
  const populateFromJobCardAuto = () => {
    if (!jobCard) return;
    
    const lines = [];
    
    // Helper to add line with auto-fetched rate
    const addLineWithRate = (particular, qty, existingRate = null) => {
      const rate = existingRate || findRateForParticular(particular);
      lines.push({ 
        particular, 
        qty: qty.toString(), 
        rate: rate > 0 ? rate.toString() : '', 
        total: 0 
      });
    };
    
    // Add pressing work from job card - Normal Pressing
    if (jobCard.normal_pressing_lines?.length > 0) {
      const normalTotal = jobCard.normal_pressing_lines.reduce((sum, l) => sum + (parseInt(l.total) || 0), 0);
      if (normalTotal > 0) {
        addLineWithRate('Pressing Work Normal', normalTotal);
      }
    }
    
    // Hot pressing lines (stored in hot_pressing_lines)
    if (jobCard.hot_pressing_lines?.length > 0) {
      const hotTotal = jobCard.hot_pressing_lines.reduce((sum, l) => sum + (parseInt(l.total) || 0), 0);
      if (hotTotal > 0) {
        addLineWithRate('Pressing Work Hot', hotTotal);
      }
    }
    
    // Veneer pressing
    if (jobCard.veneer_pressing_lines?.length > 0) {
      const veneerTotal = jobCard.veneer_pressing_lines.reduce((sum, l) => sum + (parseInt(l.total) || 0), 0);
      if (veneerTotal > 0) {
        addLineWithRate('Pressing Work Hot', veneerTotal);
      }
    }
    
    // Add cutting from job card
    if (jobCard.cutting_qty_total > 0) {
      addLineWithRate('Cutting Work', jobCard.cutting_qty_total);
    }
    
    // Add boring from job card (Mini Fix Hole Cutting)
    if (jobCard.boring_qty_total > 0) {
      addLineWithRate('Minifix Hole Cutting', jobCard.boring_qty_total);
    }
    
    // Add ALL finishing lines from job card
    if (jobCard.finishing_lines?.length > 0) {
      jobCard.finishing_lines.forEach(fl => {
        if (fl.qty && parseInt(fl.qty) > 0) {
          const particular = mapFinishingToParticular(fl.description);
          const existingRate = fl.rate && parseFloat(fl.rate) > 0 ? fl.rate : null;
          addLineWithRate(particular, fl.qty, existingRate);
        }
      });
    }
    
    if (lines.length > 0) {
      setFormData(prev => ({ ...prev, estimate_lines: lines }));
    }
  };

  // Map finishing work description to estimate particular
  const mapFinishingToParticular = (description) => {
    const mappings = {
      'Edge Banding': 'Edge Banding (22*.8)',
      'Edge Banding 2mm': 'Edge Banding (22*2)',
      'Edge Banding 45m': 'Edge Banding (45)',
      'Single Grooving': 'Handle Profile Work',
      'Double Grooving': 'Handle Profile Work',
      'Handle Profile Work': 'Handle Profile Work',
      'Edge Profile Work': 'Handle Profile',
      'Mica Edge Banding': 'Edge Banding (22*.8)',
      'Glass Door Cutting': 'Glass Door Cutting',
      'Double Work-S': 'Double Work M',
      'Double Work-M': 'Double Work M',
      'Double Work-L': 'Double Work L',
      'Double Work-XL': 'Double Work XL',
      'Mitre Cutting': 'Cutting Work',
      'Hinges Hole': 'Hinges Hole',
      'Trimming': 'Cutting Work',
      'Door Border Work': 'Handle Profile Work',
      'Mica Cutting': 'Mica Cutting',
      'Cutting List Preparation': 'Cutting List Preparation',
      'Mini Fix Hole Cutting': 'Minifix Hole Cutting',
      'Packing': 'Packing',
      'Extra Edge Band': 'Edge Banding (22*.8)',
      'Measurement Charge': 'Measurement Charge',
      'Handling Charge': 'Handling Charge',
      'Transportation': 'Transportation'
    };
    return mappings[description] || description;
  };

  return (
    <div className="bg-white border-2 border-gray-800 rounded-none font-mono text-sm max-w-2xl">
      {/* Header - Company Info */}
      <div className="bg-blue-800 text-white text-center py-3 border-b-2 border-gray-800">
        <h1 className="text-xl font-bold">INTERIOR STUDIO</h1>
        <p className="text-xs">kadavu road, Kunnumparam, Muttar</p>
      </div>

      {/* ESTIMATE Title */}
      <div className="bg-gray-200 text-center py-2 font-bold text-lg border-b-2 border-gray-800">
        ESTIMATE
      </div>

      {/* Top Info Section */}
      <div className="border-b-2 border-gray-800 grid grid-cols-2 divide-x divide-gray-400">
        <div className="divide-y divide-gray-400">
          <div className="flex">
            <label className="w-24 p-2 bg-gray-100 font-semibold border-r border-gray-400">JOB CARD</label>
            <Input 
              value={formData.job_card_number}
              onChange={(e) => updateField('job_card_number', e.target.value)}
              className="flex-1 border-0 rounded-none h-auto py-2"
              disabled
            />
          </div>
          <div className="flex">
            <label className="w-24 p-2 bg-gray-100 font-semibold border-r border-gray-400">NAME</label>
            <Input 
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              className="flex-1 border-0 rounded-none h-auto py-2"
            />
          </div>
        </div>
        <div className="divide-y divide-gray-400">
          <div className="flex">
            <label className="w-24 p-2 bg-gray-100 font-semibold border-r border-gray-400">DATE</label>
            <Input 
              type="date"
              value={formData.date}
              onChange={(e) => updateField('date', e.target.value)}
              className="flex-1 border-0 rounded-none h-auto py-2"
            />
          </div>
          <div className="flex">
            <label className="w-24 p-2 bg-gray-100 font-semibold border-r border-gray-400">REF</label>
            <Input 
              value={formData.reference}
              onChange={(e) => updateField('reference', e.target.value)}
              className="flex-1 border-0 rounded-none h-auto py-2"
            />
          </div>
        </div>
      </div>

      {/* Estimate Lines Table */}
      <div>
        <div className="bg-gray-100 p-2 flex justify-between items-center border-b border-gray-400">
          <span className="font-bold">Line Items {jobCard && <span className="text-xs text-gray-500 font-normal">(Auto-filled from Job Card)</span>}</span>
          <div className="flex gap-2">
            <div className="relative" ref={dropdownRef}>
              <Button 
                size="sm" 
                onClick={() => setShowAddDropdown(!showAddDropdown)} 
                className="h-7 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="add-row-dropdown-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Row <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
              {showAddDropdown && (
                <div className="absolute right-0 bottom-8 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-64" data-testid="add-row-dropdown-menu">
                  <button
                    onClick={addCustomLine}
                    className="w-full text-left px-3 py-2 text-xs font-semibold text-blue-600 hover:bg-blue-50 border-b border-gray-200 bg-white rounded-t-lg"
                    data-testid="add-custom-entry-option"
                  >
                    <Plus className="w-3 h-3 inline mr-1" />
                    Custom Entry
                  </button>
                  <div className="max-h-64 overflow-y-auto">
                    {PARTICULARS.map(p => (
                      <button
                        key={p}
                        onClick={() => addLine(p)}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 border-b border-gray-50"
                        data-testid={`add-particular-${p.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="border border-gray-400 p-2 w-12">SL NO</th>
              <th className="border border-gray-400 p-2">PARTICULARS</th>
              <th className="border border-gray-400 p-2 w-20">QTY</th>
              <th className="border border-gray-400 p-2 w-20">UNIT</th>
              <th className="border border-gray-400 p-2 w-24">RATE</th>
              <th className="border border-gray-400 p-2 w-28">TOTAL</th>
              <th className="border border-gray-400 p-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {formData.estimate_lines.length === 0 ? (
              <tr>
                <td colSpan="7" className="border border-gray-400 p-4 text-center text-gray-500">
                  Click "Add Row" to add estimate items
                </td>
              </tr>
            ) : (
              formData.estimate_lines.map((line, idx) => (
                <tr key={idx} className={line.isCustom ? 'bg-blue-50' : ''}>
                  <td className="border border-gray-400 p-1 text-center">{idx + 1}</td>
                  <td className="border border-gray-400 p-0">
                    {line.isCustom ? (
                      <Input
                        value={line.particular}
                        onChange={(e) => updateLine(idx, 'particular', e.target.value)}
                        placeholder="Enter custom item..."
                        className="border-0 h-8 px-2 text-sm rounded-none"
                        data-testid={`custom-particular-${idx}`}
                      />
                    ) : (
                      <select 
                        value={line.particular}
                        onChange={(e) => updateLine(idx, 'particular', e.target.value)}
                        className="w-full h-8 px-2 border-0 bg-transparent text-sm"
                        data-testid={`select-particular-${idx}`}
                      >
                        <option value="">Select item...</option>
                        {PARTICULARS.map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="border border-gray-400 p-0">
                    <Input 
                      type="number"
                      value={line.qty}
                      onChange={(e) => updateLine(idx, 'qty', e.target.value)}
                      className="border-0 h-8 text-center rounded-none"
                      data-testid={`estimate-qty-${idx}`}
                    />
                  </td>
                  <td className="border border-gray-400 p-0">
                    <select
                      value={line.unit || ''}
                      onChange={(e) => updateLine(idx, 'unit', e.target.value)}
                      className="w-full h-8 px-1 border-0 bg-transparent text-sm text-center"
                      title="Auto = taken from the rate chart when saved"
                      data-testid={`estimate-unit-${idx}`}
                    >
                      <option value="">Auto</option>
                      {['NOS', 'MTR', 'SHEET', 'BOX'].map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </td>
                  <td className="border border-gray-400 p-0">
                    <Input 
                      type="number"
                      value={line.rate}
                      onChange={(e) => updateLine(idx, 'rate', e.target.value)}
                      className="border-0 h-8 text-center rounded-none"
                      data-testid={`estimate-rate-${idx}`}
                    />
                  </td>
                  <td className="border border-gray-400 p-2 text-right font-bold bg-gray-50">
                    {((parseFloat(line.qty) || 0) * (parseFloat(line.rate) || 0)).toFixed(2)}
                  </td>
                  <td className="border border-gray-400 p-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeLine(idx)}
                      className="h-6 w-6 p-0"
                      data-testid={`remove-estimate-line-${idx}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Total Section */}
      <div className="border-t-2 border-gray-800 bg-yellow-50">
        <div className="p-3">
          <div className="flex justify-end">
            <div className="w-80">
              {/* Total Amount */}
              <div className="flex justify-between items-center p-3 bg-green-100 rounded">
                <span className="font-bold text-lg">TOTAL AMOUNT</span>
                <span className="text-xl font-bold text-green-700">
                  ₹ {calculateTotal().toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

EstimateForm.displayName = 'EstimateForm';

export default EstimateForm;
